import { useConfigStore } from '@/store/configStore';
import { cn } from '@/utils/twMerge';

type FileIconProps = React.HTMLAttributes<HTMLDivElement> & {
  fileType: string;
};

type FileTypes =
  | 'pdf'
  | 'jpg'
  | 'png'
  | 'gif'
  | 'mp4'
  | 'doc'
  | 'docx'
  | 'xls'
  | 'xlsx'
  | 'ppt'
  | 'pptx';

export const supportedFileTypes = [
  'pdf',
  'jpg',
  'png',
  'gif',
  'mp4',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
] as const;

const fileTypeColors: Record<FileTypes, string> = {
  pdf: 'bg-[#D92D20]',
  jpg: 'bg-[#0890BF]',
  png: 'bg-[#0890BF]',
  gif: 'bg-[#FF9632]',
  mp4: 'bg-[#FF9632]',
  doc: 'bg-[#2E74B5]',
  docx: 'bg-[#2E74B5]',
  xls: 'bg-[#4CA30D]',
  xlsx: 'bg-[#4CA30D]',
  ppt: 'bg-[#FF9632]',
  pptx: 'bg-[#FF9632]',
};

const gpaFileIcons = {
  pdf: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACMAAAAjCAYAAAAe2bNZAAAAAXNSR0IArs4c6QAAAERlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAAI6ADAAQAAAABAAAAIwAAAAC4vEOnAAABoklEQVRYCe2Xuy5FQRSGN9FIJDQqlXgBnVopJBpRqUSBhEIlNCcSejyCF/AGFBQqCoVCaCiERkjc4vL9sleyz94z+5J9nChmJV9m5p9ZF2tmJ0cUZe0bqdW8E3M6m6pYaXUhFu+T1HPF6ZtPmHOzWm+1hrvFXakSypyq+JQ5u8ihL1D8jTIOOvNXxSj2DHzEObYlFNlfFqPck1A6R+mDRX9Vzr4zR2eOQ9u3QjG+lofOhM74OuDTw5sJnfF1wKeHNxM64+uATw9vpm5n7JeZxluYjQOarh/a17AU61OMtmfjcLxXaTDnpJO0VRiALXiDbpCu9QhsxusxRitmlLmKEDpv5soRVXkzj0RSV+6gK4YhuoETWIcLmACzQSZD0AcvJvpGBS1ruxzcgQ5owBOk7QGhNyHOM9e/JudwCLlWpZgGkfZAnXmFtOkKdR37iY1x5veJdeWp6z6lLTgiSb+EY9A1nkEP2JvpZ+4yV47fu3cdTmvLCEdpkbW+Hl2bvqYrOAB17RTk8wy1zFl1rYhZZ2eOKl9TNmSLlbwHrOrbav+qMz+yVIkaKjLUFQAAAABJRU5ErkJggg==',
  xls: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACMAAAAjCAYAAAAe2bNZAAAAAXNSR0IArs4c6QAAAERlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAAI6ADAAQAAAABAAAAIwAAAAC4vEOnAAABt0lEQVRYCe2XPSiFURjHb8KiKAZSBikjdt1RSUoMBhkUpZTBx0JZzAwGm5XFZKKEMkvZpNS1KhJFyMfv/3Zfnds9533f+5nhPPWrc8/zP8//eZ976L6pVH78sFVuPqg5nm8Vv1PuRsJ6X1jPxNvnKsLDubulfVrleFh3sZBS4aFCziTRziH6BtVfT3JAmko1o9qT8Jn12NJGXFSyGXmPQGKPxMK4p4rIWz1qIg5UPeWbcY3cT8ZPxjUB176/M34yrgm49v2d8ZNxTcC17+9MqZNZosAVtEItHMMGDMA1tIAZU3y4g1c4g04oKmw/CRuodAu7sAIP0AZjIL2aDKOZhd6R5qEbLmAfzLB5mPm/tUuYRiGTd5jIqm3NNJHTRE5AbwNd0AhmuDxMTbB2CVXwHtRMR6C0T0apfjiAZ9ADrIEZLg9TE6xdwh2yugc3cBQo7c30kNuEOqiHbXgDM6weuoxJYhDRNAyBJqOvQO/Nj6CQeWi4x3oW2uEU+uASYiPp/5leKi3DIchgAfQXkgE9+ROoSZGBNLzAMJzDKBQV1hEWVcl9yOqRdDLusmXMRN0ZdV/V+FeT+QVT7o7H6lZ11AAAAABJRU5ErkJggg==',
  xlsx: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACMAAAAjCAYAAAAe2bNZAAAAAXNSR0IArs4c6QAAAERlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAAI6ADAAQAAAABAAAAIwAAAAC4vEOnAAABt0lEQVRYCe2XPSiFURjHb8KiKAZSBikjdt1RSUoMBhkUpZTBx0JZzAwGm5XFZKKEMkvZpNS1KhJFyMfv/3Zfnds9533f+5nhPPWrc8/zP8//eZ976L6pVH78sFVuPqg5nm8Vv1PuRsJ6X1jPxNvnKsLDubulfVrleFh3sZBS4aFCziTRziH6BtVfT3JAmko1o9qT8Jn12NJGXFSyGXmPQGKPxMK4p4rIWz1qIg5UPeWbcY3cT8ZPxjUB176/M34yrgm49v2d8ZNxTcC17+9MqZNZosAVtEItHMMGDMA1tIAZU3y4g1c4g04oKmw/CRuodAu7sAIP0AZjIL2aDKOZhd6R5qEbLmAfzLB5mPm/tUuYRiGTd5jIqm3NNJHTRE5AbwNd0AhmuDxMTbB2CVXwHtRMR6C0T0apfjiAZ9ADrIEZLg9TE6xdwh2yugc3cBQo7c30kNuEOqiHbXgDM6weuoxJYhDRNAyBJqOvQO/Nj6CQeWi4x3oW2uEU+uASYiPp/5leKi3DIchgAfQXkgE9+ROoSZGBNLzAMJzDKBQV1hEWVcl9yOqRdDLusmXMRN0ZdV/V+FeT+QVT7o7H6lZ11AAAAABJRU5ErkJggg==',
};

const FileIcon = ({ fileType, ...props }: FileIconProps) => {
  const formattedFileType = fileType.toLowerCase();

  const { config } = useConfigStore();

  const isGpa = config.ui?.generalSpecificVariant === 'gpa';

  const gpaFileIcon = gpaFileIcons[formattedFileType as keyof typeof gpaFileIcons];

  return (
    <div
      {...props}
      className={cn('relative flex w-[40px] items-center justify-center', props.className)}
    >
      {isGpa && gpaFileIcon ? (
        <img src={`${gpaFileIcon}`} alt={formattedFileType} />
      ) : (
        <>
          <span
            className={cn(
              'absolute bottom-[6px] left-[-6px] rounded-sm bg-[#0890BF] px-[3px] py-[2px] text-[10px] font-bold uppercase leading-[12px] text-white',
              fileTypeColors[formattedFileType as keyof typeof fileTypeColors]
            )}
          >
            {formattedFileType}
          </span>
          <svg
            width="32"
            height="40"
            viewBox="0 0 32 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 0.75H20C20.0911 0.75 20.1793 0.777281 20.2539 0.827148L20.3232 0.883789L31.1162 11.6768C31.2019 11.7625 31.25 11.8788 31.25 12V36C31.25 37.7949 29.7949 39.25 28 39.25H4C2.20507 39.25 0.75 37.7949 0.75 36V4C0.750001 2.20507 2.20508 0.75 4 0.75Z"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <path
              d="M20 0.5V8C20 10.2091 21.7909 12 24 12H31.5"
              stroke="currentColor"
              stroke-width="1.5"
            />
          </svg>
        </>
      )}
    </div>
  );
};

export default FileIcon;
